import { useEffect, useState } from 'react'
import { ChevronRight } from '../../../components/ChevronRight'
import { ScreenHeader } from '../../../components/ScreenHeader'
import { BackgroundTitle } from '../../../components/BackgroundTitle'
import { IngredientCardImage } from '../components/IngredientCardImage'
import { getCardOrPlaceholder } from '../data/ingredientCards'
import { useAuth } from '../../../lib/auth'
import {
  daysLeftInWeek, fetchGallery, fetchMySubmissionRow, fetchWeeklyWinners,
  getWeeklyPeriods, weekLabel, type WeeklyPeriods,
} from '../../../lib/api/weekly'
import type { WeeklySubmissionRow, WeeklyWinnerRow } from '../../../lib/database.types'
import { readError } from '../../../lib/errors'
import { WeeklyTipOverlay } from '../../../platform/weekly/WeeklyTipOverlay'
import { useWeeklyTip } from '../../../lib/api/userFlags'
import { useT } from '../../../i18n'

interface Props {
  onWriteStory: (eventId: string) => void
  onVoteOnWeek: (eventId: string) => void
  /** Back to the unified weekly landing (the tab root). */
  onBackToWeekly: () => void
  /** Open the flat past-weeks archive list. */
  onOpenArchive: () => void
}

/**
 * Weekly event hub — always offers two actions: write this week's story
 * and vote on last week's. First-ever week hides the voting card since
 * there's nothing finalized yet.
 */
export function WeeklyHubScreen({
  onWriteStory, onVoteOnWeek,
  onBackToWeekly, onOpenArchive,
}: Props) {
  const t = useT()
  const { profile, session, configured } = useAuth()
  // While the live event is in test mode anyone with a Supabase
  // session — including anonymous "guests" — can submit. The RPC
  // keys submissions to auth.uid(), which guests have via
  // signInAnonymously, so this stays safe.
  const signedIn = Boolean(session && profile)
  const canSubmit = signedIn
  const [periods, setPeriods] = useState<WeeklyPeriods | null>(null)
  const [mySub, setMySub] = useState<WeeklySubmissionRow | null>(null)
  const [writeEntryCount, setWriteEntryCount] = useState<number | null>(null)
  const [voteEntryCount, setVoteEntryCount] = useState<number | null>(null)
  const [winners, setWinners] = useState<WeeklyWinnerRow[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { showTip, dismissTip } = useWeeklyTip('ideatthat')

  useEffect(() => {
    if (!configured) { setLoading(false); return }
    let cancelled = false
    async function load() {
      try {
        const p = await getWeeklyPeriods()
        if (cancelled) return
        setPeriods(p)

        // Read-only for everyone: the hub never creates a submission
        // row. It used to ensure one for each signed-in visitor, and
        // after the Sunday close those placeholder rows dressed the
        // winners board with phantom entrants. The editor owns row
        // creation now.
        const [sub, writeSubs, voteSubs, pastWinners] = await Promise.all([
          canSubmit && profile ? fetchMySubmissionRow(p.write.id, profile.id) : Promise.resolve(null),
          fetchGallery(p.write.id),
          p.vote ? fetchGallery(p.vote.id) : Promise.resolve(null),
          fetchWeeklyWinners(p.vote?.week_start),
        ])
        if (cancelled) return
        setMySub(sub)
        // Only count submissions that actually contain a story —
        // ensure_weekly_submission creates an empty placeholder row
        // the moment a guest taps in, so the raw `.length` over-
        // counts every drive-by anonymous user. Match the same
        // "real story" predicate `myStoryDone` uses below.
        const hasStory = (s: { title: string; course1_text: string }) =>
          s.title.trim().length > 0 || s.course1_text.trim().length > 10
        setWriteEntryCount(writeSubs.filter(hasStory).length)
        if (voteSubs) setVoteEntryCount(voteSubs.filter(hasStory).length)
        setWinners(pastWinners)
      } catch (e) {
        if (!cancelled) setError(readError(e))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [configured, signedIn, canSubmit, profile?.id])

  // ── Loading / setup gates ──────────────────────────────────────────────
  if (!configured) {
    return (
      <HubShell onBackToWeekly={onBackToWeekly}>
        <div className="card p-5 mx-5">
          <p className="font-semibold text-brand-text">{t("Supabase isn't configured")}</p>
          <p className="text-sm text-brand-muted mt-1">
            {t('The weekly event needs the backend to be set up. See')} <span className="font-mono">{t('SUPABASE_SETUP.md')}</span>.
          </p>
        </div>
      </HubShell>
    )
  }

  if (loading || !periods) {
    return (
      <HubShell onBackToWeekly={onBackToWeekly}>
        <div className="px-5 space-y-3">
          <div className="h-32 rounded-2xl bg-brand-elevated animate-pulse" />
          <div className="h-16 rounded-2xl bg-brand-elevated animate-pulse" />
          <div className="h-16 rounded-2xl bg-brand-elevated animate-pulse" />
        </div>
      </HubShell>
    )
  }

  const myStoryDone =
    (mySub?.title.trim().length ?? 0) > 0 &&
    (mySub?.course1_text.trim().length ?? 0) > 10

  const sharedCards = periods.write.shared_ingredients.map(id => getCardOrPlaceholder(id))
  const myRandoms = (mySub?.random_ingredients ?? []).map(id => getCardOrPlaceholder(id))

  return (
    <HubShell onBackToWeekly={onBackToWeekly}>
      <div className="px-5 space-y-5 overflow-y-auto pb-6">
        {error && (
          <div className="card p-3 bg-brand-danger/10 border border-brand-danger/30">
            <p className="text-sm text-brand-danger">{error}</p>
          </div>
        )}

        {/* ── This week — write phase ────────────────────────────────── */}
        <div className="event-card">
          <div className="flex items-start justify-between mb-4">
            <div>
              <p className="text-xs font-semibold text-game-iet uppercase tracking-wide mb-1">
                {t('This Week · Write')}
              </p>
              <h2 className="font-display text-xl font-bold text-brand-text">
                {weekLabel(periods.write.week_start)}
              </h2>
            </div>
            <div className="countdown-pill">
              {daysLeftInWeek(periods.write.week_start)}d left
            </div>
          </div>

          {/* Shared ingredients — render the real game cards (printed
              art) instead of small chips so the preview reads as the
              actual deck the player will write about. */}
          <div className="mb-4">
            <p className="text-xs font-medium text-brand-muted uppercase tracking-wide mb-2">
              {t('Everyone gets these')}
            </p>
            <div className="flex flex-wrap gap-3">
              {sharedCards.map(card => card && (
                <IngredientCardImage key={card.id} id={card.id} size="md" />
              ))}
            </div>
          </div>

          {/* Bonus card — the per-player random ingredient. We hide
              the actual card behind a "?" placeholder until the
              player has actually submitted their story (so the
              reveal lands as a reward for engaging, and so guests
              don't see a randomly-assigned ingredient before they've
              committed anything). */}
          <div className="mb-4">
            <p className="text-xs font-medium text-brand-muted uppercase tracking-wide mb-2">
              {t('Your bonus card')}
            </p>
            <div className="flex flex-wrap gap-3">
              {myStoryDone && myRandoms.length > 0 ? (
                myRandoms.map(card => card && (
                  <IngredientCardImage key={card.id} id={card.id} size="md" />
                ))
              ) : (
                <PlaceholderCard size="md" />
              )}
            </div>
          </div>

          <button
            onClick={() => canSubmit && onWriteStory(periods.write.id)}
            disabled={!canSubmit}
            className={myStoryDone ? 'btn-iet-outline' : 'btn-iet animate-pulse-glow'}
          >
            {myStoryDone ? t('Edit your dish') : t('Write your food story')}
          </button>
          {writeEntryCount !== null && (
            <p className="text-xs text-brand-muted text-center mt-3">
              {writeEntryCount} storyteller{writeEntryCount === 1 ? '' : 's'} writing this week
            </p>
          )}
        </div>

        {/* ── Last week — vote phase ─────────────────────────────────── */}
        {periods.vote && voteEntryCount !== null && voteEntryCount > 0 ? (
          <button
            onClick={() => onVoteOnWeek(periods.vote!.id)}
            className="event-card w-full text-left active:scale-[0.98] transition-transform"
          >
            <div className="flex items-start justify-between mb-2">
              <div>
                <p className="text-xs font-semibold text-iet-green uppercase tracking-wide mb-1">
                  {t('Last Week · Vote')}
                </p>
                <h2 className="font-display text-lg font-bold text-brand-text">
                  {weekLabel(periods.vote.week_start)}
                </h2>
              </div>
              <div className="text-2xl">👍</div>
            </div>
            <p className="text-sm text-brand-muted">
              {voteEntryCount} stor{voteEntryCount === 1 ? 'y' : 'ies'} ready to read
              and vote on. Tap the ones you'd actually eat.
            </p>
          </button>
        ) : (
          <div className="card p-4 opacity-80">
            <p className="text-xs font-semibold text-brand-muted uppercase tracking-wide mb-1">
              {t('Last Week · Vote')}
            </p>
            <p className="text-sm text-brand-muted">{t('No finalised stories from last week yet. The voting card will appear here once a previous week is locked.')}</p>
          </div>
        )}

        {/* ── Winners archive — only renders when there's at least one
            truly-past week to look at. With only 1-2 weeks of history
            and the current vote-week excluded, the archive is often
            empty; showing a button that opens an empty page reads as
            broken, so we hide it until past weeks exist. */}
        {winners && winners.length > 0 && (
          <button
            onClick={onOpenArchive}
            className="w-full iet-gold border-2 border-game-iet-dark
                       rounded-2xl shadow-card p-4 flex items-center gap-3 text-left
                       active:scale-[0.98] transition-transform"
          >
            <div className="flex-1">
              <p className="font-semibold text-brand-text">{t('Winners archive')}</p>
              <p className="text-xs text-brand-muted">
                {winners.length} past week{winners.length === 1 ? '' : 's'}
              </p>
            </div>
            <ChevronRight color="#B58615" />
          </button>
        )}
      </div>

      {showTip && <WeeklyTipOverlay game="ideatthat" onDismiss={dismissTip} />}
    </HubShell>
  )
}

/**
 * A face-blank card that matches the IngredientCardImage footprint —
 * same aspect ratio, same height presets, dashed outline + a centered
 * "?". Used as the bonus-card stand-in until the player commits
 * a story.
 */
function PlaceholderCard({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' | 'xl' }) {
  const t = useT()
  const heightClass = {
    sm: 'h-16',
    md: 'h-24',
    lg: 'h-32',
    xl: 'h-44',
  }[size]
  return (
    <div className="flex flex-col items-center">
      <div
        className={`relative aspect-597/822 ${heightClass} rounded-xl
                    border-2 border-dashed border-game-iet-dark/60
                    bg-white/40 flex items-center justify-center`}
      >
        <span className="font-display text-3xl font-bold text-game-iet-dark/70">?</span>
      </div>
      <p className="text-xs max-w-20 font-medium text-brand-muted text-center truncate mt-1">
        {t('Bonus')}
      </p>
    </div>
  )
}

function HubShell({
  onBackToWeekly, children,
}: {
  onBackToWeekly: () => void
  children: React.ReactNode
}) {
  const t = useT()
  return (
    <div className="screen pb-24">
      <BackgroundTitle text={t("Weekly I'd Eat That")} color="#B58615" />
      <ScreenHeader
        image="/games/ideatthat.webp"
        imageAlt="I'd Eat That"
        leftSlot={
          <button
            onClick={onBackToWeekly}
            className="w-9 h-9 flex items-center justify-center rounded-full
                       bg-brand-elevated active:bg-brand-border transition-colors"
            aria-label={t('Back to weekly')}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M15 18l-6-6 6-6" stroke="#111" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        }
      />
      <div className="relative z-10">{children}</div>
    </div>
  )
}

